import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker, Polyline, InfoWindow } from '@react-google-maps/api';
import "./css/GoogleMapEmbed.css";
import { env } from 'process';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useGoogleMaps } from '../../../contexts/GoogleMapsContext';

// Colors
const ROUTE_COLORS = [
    '#FF5733', '#33FF57', '#3357FF', '#FF33F5', '#F5FF33',
    '#33FFF5', '#FF5733', '#8C33FF', '#FF8C33', '#33FF8C',
    '#338CFF', '#FF338C', '#8CFF33', '#F533FF', '#33F5FF',
    '#FFA333', '#33FFA3', '#A333FF', '#FFA3A3', '#A3FFA3'
];

const CIRCUIT_COLORS = [
    '#FF0000', '#00FF00', '#0000FF', '#FFA500', '#800080',
    '#00FFFF', '#FF00FF', '#FFD700', '#008000', '#4B0082',
];

// Marker paths
const MARKER_PATHS = {
    depotMarker: '/img/map-markers/depot-marker.svg',
    chargerMarker: '/img/map-markers/charger-marker.svg',
    selectedDepotMarker: '/img/map-markers/depot-marker-selected.svg',
    selectedChargerMarker: '/img/map-markers/charger-marker-selected.svg',
    markerShadow: '/img/map-markers/marker-shadow.png'
};

// ==== Marker Icons ====
const createMarkerIcons = () => {
    if (!window.google) return null;

    return {
        charger: {
            default: {
                url: MARKER_PATHS.chargerMarker,
                scaledSize: new window.google.maps.Size(30, 49),
                anchor: new window.google.maps.Point(15, 38),
                labelOrigin: new window.google.maps.Point(15, -10),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(49, 49),
                    anchor: new window.google.maps.Point(15, 49)
                }
            },
            selected: {
                url: MARKER_PATHS.selectedChargerMarker,
                scaledSize: new window.google.maps.Size(30, 49),
                anchor: new window.google.maps.Point(15, 38),
                labelOrigin: new window.google.maps.Point(15, -10),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(49, 49),
                    anchor: new window.google.maps.Point(15, 49)
                }
            }
        },
        depot: {
            default: {
                url: MARKER_PATHS.depotMarker,
                scaledSize: new window.google.maps.Size(45, 73),
                anchor: new window.google.maps.Point(22, 58),
                labelOrigin: new window.google.maps.Point(22, -15),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(73, 73),
                    anchor: new window.google.maps.Point(22, 73)
                }
            },
            selected: {
                url: MARKER_PATHS.selectedDepotMarker,
                scaledSize: new window.google.maps.Size(45, 73),
                anchor: new window.google.maps.Point(22, 58),
                labelOrigin: new window.google.maps.Point(22, -15),
                shadow: {
                    url: MARKER_PATHS.markerShadow,
                    scaledSize: new window.google.maps.Size(73, 73),
                    anchor: new window.google.maps.Point(22, 73)
                }
            }
        }
    };
};

const MapControls = ({
    routeCheckbox, setRouteCheckbox,
    depotCheckbox, setDepotCheckbox,
    chargerCheckbox, setChargerCheckbox,
}) => {
    return (
        <div className="map-overlay">
            <h3>Features</h3>
            <label className="overlay-entry">
                <input
                    type="checkbox"
                    className="checkbox-overlay"
                    checked={routeCheckbox}
                    onChange={(e) => setRouteCheckbox(e.target.checked)}
                />
                <span className="map-checkbox-description">Routes</span>
            </label>
            <label className="overlay-entry">
                <input
                    type="checkbox"
                    className="checkbox-overlay"
                    checked={depotCheckbox}
                    onChange={(e) => setDepotCheckbox(e.target.checked)}
                />
                <span className="map-checkbox-description flex flex-row">
                    <span>Depots</span>
                    <img
                        src={MARKER_PATHS.selectedDepotMarker}
                        alt="Depot"
                        style={{ width: "20px", height: "20px", marginLeft: "5px" }}
                    />
                </span>
            </label>
            <label className="overlay-entry">
                <input
                    type="checkbox"
                    className="checkbox-overlay"
                    checked={chargerCheckbox}
                    onChange={(e) => setChargerCheckbox(e.target.checked)}
                />
                <span className="map-checkbox-description flex flex-row">
                    <span>Charging Stations</span>
                    <img
                        src={MARKER_PATHS.chargerMarker}
                        alt="Chargers"
                        style={{ width: "15px", height: "15px", marginLeft: "5px" }}
                    />
                </span>
            </label>
        </div>
    );
};

// Routes layer
const RoutesLayer = ({ routeData, highlightedRoute, routeColorMap, setRouteColorMap }) => {
    const [fetchedRouteData, setFetchedRouteData] = useState([]);

    // Initialize the color map if it doesn't exist yet
    useEffect(() => {
        if (!routeColorMap || Object.keys(routeColorMap).length === 0) {
            const newColorMap = {};
            Object.keys(routeData || {}).forEach((routeId, index) => {
                newColorMap[routeId] = ROUTE_COLORS[index % ROUTE_COLORS.length];
            });
            if (Object.keys(newColorMap).length > 0) {
                setRouteColorMap(newColorMap);
            }
        }
    }, [routeData, routeColorMap, setRouteColorMap]);

    // Fetch route data
    useEffect(() => {
        const fetchRouteData = async () => {
            try {
                const response = await axios.post(
                    API_ENDPOINTS.getBatchRawRoutes,
                    { route_ids: Object.keys(routeData) },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${localStorage.getItem('authToken')}`
                        }
                    }
                );
                console.log("Fetched route data:", response.data);
                setFetchedRouteData(response.data);
            } catch (error) {
                console.error("Error fetching route data:", error);
            }
        };

        if (routeData && Object.keys(routeData).length > 0) {
            fetchRouteData();
        }
    }, [routeData]);

    if (!routeData || typeof routeData !== 'object') {
        console.error("Invalid route data:", routeData);
        return null;
    }

    return (
        <>
            {Object.entries(routeData)
                .filter(([_, data]) => data && data.selected)
                .map(([routeId, data]) => (
                    <RoutePolylines
                        key={`route-${routeId}`}
                        routeId={parseInt(routeId)}
                        geometry={data.geometry}
                        highlightedRoute={highlightedRoute}
                        routeColor={routeColorMap[routeId] || ROUTE_COLORS[0]}
                        routeData={data}
                        infoWindowData={fetchedRouteData}
                    />
                ))}
        </>
    );
};

// Create a new component to handle rendering route polylines
const RoutePolylines = ({ routeId, geometry, highlightedRoute, routeColor, routeData, infoWindowData }) => {
    const [showInfoWindow, setShowInfoWindow] = useState(false);
    const [clickPosition, setClickPosition] = useState(null);

    // Ensure infoWindowData is an array and find the route info
    const routeInfo = Array.isArray(infoWindowData) ? infoWindowData.find(route => route.id === routeId) : null;

    if (!geometry || (Array.isArray(geometry) && geometry.length === 0)) {
        console.error(`No geometry data for route ${routeId}`);
        return null;
    }

    const handleRouteClick = (e) => {
        setClickPosition({
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
        });
        setShowInfoWindow(true);
    };

    return (
        <>
            {Array.isArray(geometry) && geometry.map((link, linkIndex) => {
                if (!link.coordinates || !Array.isArray(link.coordinates) || link.coordinates.length < 2) {
                    return null;
                }

                // Convert coordinates to Google Maps format
                const path = link.coordinates.map(coord => ({
                    lat: coord[1],
                    lng: coord[0]
                }));

                return (
                    <React.Fragment key={`${routeId}-${linkIndex}`}>
                        {/* Route Polyline */}
                        <Polyline
                            path={path}
                            options={{
                                strokeColor: routeColor,
                                strokeOpacity: 1,
                                strokeWeight: 6,
                                clickable: true,
                                zIndex: highlightedRoute === routeId ? 2 : 1
                            }}
                            onClick={handleRouteClick}
                        />

                        {/* Start Point Marker */}
                        <Marker
                            position={path[0]}
                            icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 6,
                                fillColor: "#ffffff",
                                fillOpacity: 1,
                                strokeColor: "#000000",
                                strokeWeight: 2
                            }}
                        />

                        {/* End Point Marker */}
                        <Marker
                            position={path[path.length - 1]}
                            icon={{
                                path: window.google.maps.SymbolPath.CIRCLE,
                                scale: 6,
                                fillColor: "#ffffff",
                                fillOpacity: 1,
                                strokeColor: "#000000",
                                strokeWeight: 2
                            }}
                        />
                    </React.Fragment>
                );
            })}

            {showInfoWindow && clickPosition && (
                <InfoWindow
                    position={clickPosition}
                    onCloseClick={() => setShowInfoWindow(false)}
                >
                    <div className="route-info-window">
                        <h3>Route {routeInfo?.raw_route_name || 'Unknown'}</h3>
                        <div className="route-details">
                            <p><strong>Distance:</strong> {routeInfo?.distances?.reduce((sum, distance) => sum + distance / 1609.34, 0).toFixed(1) || 'N/A'} miles</p>
                            <p><strong>Duration:</strong> {routeInfo?.travel_time?.reduce((sum, time) => sum + time, 0).toFixed(2) || 'N/A'} min</p>
                            <p><strong>Stops:</strong>
                                <ul>
                                    {Array.isArray(routeInfo?.addresses) ? (
                                        routeInfo.addresses.map((address, index) => (
                                            <li key={index}>{address}</li>
                                        ))
                                    ) : (
                                        <li>N/A</li>
                                    )}
                                </ul>
                            </p>
                        </div>
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

// Circuit lines renderer
const CircuitLinesLayer = ({ selectedEntities, circuitData }) => {
    const [selectedCircuit, setSelectedCircuit] = useState(null);
    const [infoPosition, setInfoPosition] = useState(null);

    useEffect(() => {
        console.log("Circuit data updated:", circuitData);
    }, [circuitData]);

    const handleCircuitClick = (e, circuit) => {
        // Get the clicked position
        const clickedPosition = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
        };

        setInfoPosition(clickedPosition);
        setSelectedCircuit(circuit);
    };

    const renderCircuitLines = () => {
        const lines = [];
        Object.entries(selectedEntities).forEach(([depotId, isSelected]) => {
            if (isSelected && circuitData[depotId]) {
                circuitData[depotId].forEach((circuit, circuitIndex) => {
                    const circuitColor = CIRCUIT_COLORS[circuitIndex % CIRCUIT_COLORS.length];

                    // Safety check to ensure we have valid geometry
                    if (!circuit.geometry || !circuit.geometry.coordinates) {
                        console.warn(`Missing geometry for circuit ${circuit.circuit_name}`);
                        return;
                    }

                    // Ensure coordinates is an array before proceeding
                    if (!Array.isArray(circuit.geometry.coordinates)) {
                        console.warn(`Invalid coordinates for circuit ${circuit.circuit_name}:`, circuit.geometry.coordinates);
                        return;
                    }

                    circuit.geometry.coordinates.forEach((lineString, lineIndex) => {
                        // Ensure lineString is an array of coordinates
                        if (!Array.isArray(lineString)) {
                            console.warn(`Invalid lineString for circuit ${circuit.circuit_name}:`, lineString);
                            return;
                        }

                        // Convert coordinates to Google Maps format
                        const path = lineString.map(coord => ({
                            lat: coord[1],
                            lng: coord[0]
                        }));

                        lines.push(
                            <Polyline
                                key={`circuit-${depotId}-${circuitIndex}-${lineIndex}`}
                                path={path}
                                options={{
                                    strokeColor: circuitColor,
                                    strokeWeight: 3,
                                    strokeOpacity: 0.8,
                                    clickable: true
                                }}
                                onClick={(e) => handleCircuitClick(e, circuit)}
                            />
                        );
                    });
                });
            }
        });
        return lines;
    };

    return (
        <>
            {renderCircuitLines()}

            {selectedCircuit && infoPosition && (
                <InfoWindow
                    position={infoPosition}
                    onCloseClick={() => setSelectedCircuit(null)}
                >
                    <div className="marker-info-window">
                        <h5>Circuit</h5>
                        <table>
                            <tbody>
                                <tr>
                                    <td>Name</td>
                                    <td>{selectedCircuit.circuit_name}</td>
                                </tr>
                                <tr>
                                    <td>Capacity</td>
                                    <td>{selectedCircuit.capacity} kW</td>
                                </tr>
                                <tr>
                                    <td>Voltage</td>
                                    <td>{selectedCircuit.circuit_voltage} kV</td>
                                </tr>
                                <tr>
                                    <td>Substation</td>
                                    <td>{selectedCircuit.substation_name?.replace(/Kv/g, 'kV')}</td>
                                </tr>
                                <tr>
                                    <td>Last Updated</td>
                                    <td>{new Date(selectedCircuit.date_last_update).toLocaleDateString()}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

// Depot markers layer
const DepotMarkersLayer = ({ depots, selectedEntities, markerIcons, handleMarkerClick, getMarkerIcon }) => {
    const [selectedDepot, setSelectedDepot] = useState(null);

    if (!markerIcons) return null;

    return (
        <>
            {depots.map((depot, index) => (
                <Marker
                    key={`depot-${depot.depot_id || index}`}
                    position={{ lat: depot.lat, lng: depot.lon }}
                    icon={getMarkerIcon(depot, 'depot', markerIcons)}
                    zIndex={selectedEntities[depot.depot_id] ? 1000 : 0}
                    onClick={() => setSelectedDepot(depot)}
                />
            ))}

            {selectedDepot && (
                <InfoWindow
                    position={{ lat: selectedDepot.lat, lng: selectedDepot.lon }}
                    onCloseClick={() => setSelectedDepot(null)}
                >
                    <div className="marker-info-window">
                        <h5>Depot</h5>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '30%' }} />
                                <col style={{ width: '70%' }} />
                            </colgroup>
                            <tbody>
                                <tr><td>Name:</td><td>{selectedDepot.name}</td></tr>
                                <tr><td>Address:</td><td>{selectedDepot.address}</td></tr>
                            </tbody>
                        </table>
                        <div style={{ fontSize: '1.2rem', fontStyle: 'italic' }}>Click to view circuit lines on map</div>
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

// Charger markers layer
const ChargerMarkersLayer = ({ chargers, selectedChargers, markerIcons, handleChargerClick, getMarkerIcon }) => {
    const [selectedCharger, setSelectedCharger] = useState(null);

    const handleMarkerClick = (charger) => {
        setSelectedCharger(charger);
        handleChargerClick(charger.cs_id, true);
    };

    if (!markerIcons) return null;

    return (
        <>
            {chargers.map((charger, index) => (
                <Marker
                    key={`charger-${charger.cs_id || index}-${charger.lat}-${charger.lon}`}
                    position={{ lat: charger.lat, lng: charger.lon }}
                    icon={getMarkerIcon(charger, 'charger', markerIcons)}
                    zIndex={selectedChargers[charger.cs_id] ? 1000 : 0}
                    onClick={() => handleMarkerClick(charger)}
                />
            ))}

            {selectedCharger && (
                <InfoWindow
                    position={{ lat: selectedCharger.lat, lng: selectedCharger.lon }}
                    onCloseClick={() => setSelectedCharger(null)}
                >
                    <div className="marker-info-window">
                        <h5>Charging Station</h5>
                        <table style={{ width: '100%', tableLayout: 'fixed' }}>
                            <colgroup>
                                <col style={{ width: '40%' }} />
                                <col style={{ width: '60%' }} />
                            </colgroup>
                            <tbody>
                                <tr><td>Owner</td><td>{selectedCharger.owner}</td></tr>
                                <tr><td>Address</td><td>{selectedCharger.addr}</td></tr>
                                <tr><td>Status</td><td>{selectedCharger.status}</td></tr>
                                <tr><td>Charger Count</td><td>{selectedCharger.stall_count}</td></tr>
                                <tr><td>Max Power (kW)</td><td>{selectedCharger.max_power}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </InfoWindow>
            )}
        </>
    );
};

// Main component
export const GoogleMapEmbed = ({
    selectedRoutes,
    selectedEntities = {},
    selectedChargers = {},
    circuitData = {},
    onMarkerClick,
    onChargerClick,
    onRouteClick,
    highlightedRoute,
    depotList = [],
    chargerList = [],
    refreshTrigger = 0,
    loadingCircuits = false
}) => {
    /* 
     Args:
       selectedRoutes: object of route ids as keys and route objects as values
       selectedEntities: object with depot ids as keys and boolean values
       selectedChargers: object with charger ids as keys and boolean values
       circuitData: object with depot ids as keys and arrays of circuit objects as values
       onMarkerClick: function to handle marker clicks
       onChargerClick: function to handle charger clicks
       onRouteClick: function to handle route clicks
       highlightedRoute: route id of the highlighted route
       depotList: array of depot objects
       chargerList: array of charger objects
       refreshTrigger: number to trigger a refresh of the map
       loadingCircuits: boolean to indicate if circuits are still loading
   */

    // Get the Google Maps loading state from context
    const { isLoaded, loadError } = useGoogleMaps();

    const [routeCheckbox, setRouteCheckbox] = useState(true);
    const [depotCheckbox, setDepotCheckbox] = useState(true);
    const [chargerCheckbox, setChargerCheckbox] = useState(true);

    const [routeColorMap, setRouteColorMap] = useState({});
    const [markerIcons, setMarkerIcons] = useState(null);

    // Handle loading and error states
    if (loadError) {
        return <div className="map-error">Error loading maps</div>;
    }

    if (!isLoaded) {
        return <div className="map-loading">Loading maps...</div>;
    }

    // Initialize marker icons when Google Maps is loaded
    if (isLoaded && !markerIcons) {
        setMarkerIcons(createMarkerIcons());
    }

    // Helper functions
    const getMarkerIcon = (entity, type) => {
        const isSelected = selectedEntities[entity.depot_id || entity.charger_id];
        return markerIcons[type][isSelected ? 'selected' : 'default'];
    };

    // Event handlers
    const handleMarkerClick = (depotId, fromMap = false) => {
        // Check if we received an object (batch selection from table)
        if (typeof depotId === 'object') {
            // This is a batch operation from the "select all" checkbox
            if (onMarkerClick) onMarkerClick(depotId, true);
        } else {
            // This is a single selection
            if (onMarkerClick) onMarkerClick(depotId, fromMap);
        }
    };

    const handleChargerClick = (chargerId, fromMap = false) => {
        // Check if we received an object (batch selection from table)
        if (typeof chargerId === 'object') {
            // This is a batch operation from the "select all" checkbox
            if (onChargerClick) onChargerClick(chargerId, true);
        } else {
            // This is a single selection
            if (onChargerClick) onChargerClick(chargerId, fromMap);
        }
    };

    // Add a loading indicator for circuits
    const CircuitLoadingIndicator = () => {
        if (!loadingCircuits) return null;

        return (
            <div className="circuit-loading-indicator">
                <div className="loading-spinner"></div>
                <span>Loading circuit data...</span>
            </div>
        );
    };

    console.log("Google Map inputs:", {
        selectedRoutes,
        selectedEntities,
        selectedChargers,
        circuitData,
        onMarkerClick,
        onChargerClick,
        onRouteClick,
        highlightedRoute,
        depotList,
        chargerList,
        refreshTrigger,
        loadingCircuits
    })

    return (
        <div className="map-dashboard-container map-dashboard-top google">
            <div className="map-container">
                <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={{ lat: 33.9, lng: -117.5 }}
                    zoom={10}
                >
                    <CircuitLoadingIndicator />
                    {depotCheckbox && <CircuitLinesLayer
                        selectedEntities={selectedEntities}
                        circuitData={circuitData}
                    />}
                    {routeCheckbox && <RoutesLayer
                        routeData={selectedRoutes}
                        highlightedRoute={highlightedRoute}
                        routeColorMap={routeColorMap}
                        setRouteColorMap={setRouteColorMap}
                    />}
                    {depotCheckbox && <DepotMarkersLayer
                        depots={depotList}
                        selectedEntities={selectedEntities}
                        markerIcons={markerIcons}
                        handleMarkerClick={handleMarkerClick}
                        getMarkerIcon={getMarkerIcon}
                    />}
                    {chargerCheckbox && <ChargerMarkersLayer
                        chargers={chargerList}
                        selectedChargers={selectedChargers}
                        markerIcons={markerIcons}
                        handleChargerClick={handleChargerClick}
                        getMarkerIcon={getMarkerIcon}
                    />}
                </GoogleMap>

                <MapControls
                    routeCheckbox={routeCheckbox}
                    setRouteCheckbox={setRouteCheckbox}
                    depotCheckbox={depotCheckbox}
                    setDepotCheckbox={setDepotCheckbox}
                    chargerCheckbox={chargerCheckbox}
                    setChargerCheckbox={setChargerCheckbox}
                />
            </div>
        </div>
    )
};